import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import luxonPlugin from "@fullcalendar/luxon3";
import timeGridPlugin from "@fullcalendar/timegrid";
import { DateTime } from "luxon";
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock3,
  Eye,
  Info,
  List,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { inspectionsAPI, projectsAPI } from "../../../services/api";
import { walkSchedulesAPI } from "../../../services/walkSchedulesAPI";
import { useProjectContext } from "../../../hooks/useProjectContext";
import LoadingSpinner from "../../common/LoadingSpinner";
import WalkScheduleEditModal from "./WalkScheduleEditModal";

const MEETING_TYPES = [
  ["call", "Call"],
  ["site_walk", "Site walk"],
  ["inspection_review", "Inspection review"],
  ["finding_clarification", "Finding clarification"],
  ["reinspection", "Reinspection"],
];

const emptyRequest = {
  requested_user_ids: [],
  cc_user_ids: [],
  inspection_id: "",
  meeting_type: "site_walk",
  title: "",
  location: "",
  schedule_date: "",
  start_time: "",
  end_time: "",
  notes: "",
};

const emptyBusy = {
  id: null,
  schedule_date: "",
  start_time: "",
  end_time: "",
  recurrence_type: "none",
  repeat_until: "",
  note: "",
};

const CALENDAR_VIEWS = [
  {
    id: "timeGridDay",
    label: "Day",
    hint: "One-day hourly view",
    icon: CalendarDays,
  },
  {
    id: "timeGridWeek",
    label: "Work week",
    hint: "Monday to Friday",
    icon: CalendarClock,
  },
  {
    id: "dayGridMonth",
    label: "Month",
    hint: "Monthly overview",
    icon: CalendarDays,
  },
  {
    id: "listMonth",
    label: "Agenda",
    hint: "Upcoming 30-day list",
    icon: List,
  },
];

const roleLabel = (role) =>
  String(role || "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const memberId = (member) => Number(member?.user_id ?? member?.id);
const memberName = (member) =>
  `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
  member?.email ||
  `User #${memberId(member)}`;

// Name A-Z, then role/email/id for deterministic duplicate-name ordering.
const compareMembers = (left, right) =>
  memberName(left).localeCompare(memberName(right), undefined, { sensitivity: "base", numeric: true }) ||
  roleLabel(left.role).localeCompare(roleLabel(right.role), undefined, { sensitivity: "base" }) ||
  String(left.email || "").localeCompare(String(right.email || "")) ||
  memberId(left) - memberId(right);

const toUtcIso = (date, time, zone) => {
  if (!date || !time) return null;
  const value = DateTime.fromISO(`${date}T${time}`, { zone: zone || "UTC" });
  return value.isValid ? value.toUTC().toISO() : null;
};

const localFields = (value, zone) => {
  const dt = DateTime.fromISO(value || "", { zone: "utc" }).setZone(zone || "UTC");
  if (!dt.isValid) return { date: "", time: "" };
  return { date: dt.toISODate(), time: dt.toFormat("HH:mm") };
};

const formatWindow = (schedule, zone) => {
  const start = DateTime.fromISO(schedule?.preferred_start_at || "", {
    zone: "utc",
  }).setZone(zone || "UTC");
  const end = DateTime.fromISO(schedule?.preferred_end_at || "", {
    zone: "utc",
  }).setZone(zone || "UTC");
  if (!start.isValid) return "—";
  return `${start.toFormat("ccc, LLL d · h:mm a")} – ${
    end.isValid ? end.toFormat("h:mm a") : "—"
  }`;
};

const relativeDateLabel = (value, zone) => {
  const target = DateTime.fromISO(value || "", { zone: "utc" }).setZone(
    zone || "UTC",
  );
  const today = DateTime.now().setZone(zone || "UTC").startOf("day");
  if (!target.isValid) return "Date unavailable";
  const dayDifference = Math.round(target.startOf("day").diff(today, "days").days);
  if (dayDifference === 0) return "Today";
  if (dayDifference === 1) return "Tomorrow";
  if (dayDifference === -1) return "Yesterday";
  return target.toFormat("ccc, LLL d");
};

const scheduleColor = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "accepted":
      return "#059669";
    case "rejected":
      return "#dc2626";
    case "cancelled":
      return "#6b7280";
    case "proposed_new_time":
      return "#2563eb";
    default:
      return "#ea580c";
  }
};

const participantResponse = (schedule, userId) =>
  (schedule?.requested_users || []).find(
    (participant) => Number(participant.user_id) === Number(userId),
  ) || null;

const responseSummary = (schedule) => {
  const participants = schedule?.requested_users || [];
  return participants.reduce(
    (summary, participant) => {
      const status = String(participant.response_status || "requested").toLowerCase();
      summary.total += 1;
      if (Object.prototype.hasOwnProperty.call(summary, status)) {
        summary[status] += 1;
      } else {
        summary.requested += 1;
      }
      return summary;
    },
    { total: 0, requested: 0, accepted: 0, rejected: 0, proposed_new_time: 0 },
  );
};

const responseBadgeClass = (status) => {
  switch (String(status || "requested").toLowerCase()) {
    case "accepted":
      return "bg-emerald-100 text-emerald-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    case "proposed_new_time":
      return "bg-blue-100 text-blue-700";
    default:
      return "bg-orange-100 text-orange-700";
  }
};

function MultiPersonSelect({ options, selectedIds, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = useMemo(
    () => new Set((selectedIds || []).map(Number)),
    [selectedIds],
  );
  const selectedMembers = useMemo(
    () => options.filter((option) => selected.has(memberId(option))),
    [options, selected],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((member) =>
      [memberName(member), member.email, member.role, roleLabel(member.role)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [options, query]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="min-h-11 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-gray-400"
      >
        {selectedMembers.length ? (
          <span className="flex flex-wrap gap-1.5">
            {selectedMembers.slice(0, 3).map((member) => (
              <span
                key={memberId(member)}
                className="rounded-full bg-orange-50 px-2 py-1 text-xs font-medium text-orange-700"
              >
                {memberName(member)}
              </span>
            ))}
            {selectedMembers.length > 3 && (
              <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                +{selectedMembers.length - 3}
              </span>
            )}
          </span>
        ) : (
          <span className="text-gray-400">{placeholder}</span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, email, or role"
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.map((member) => {
              const id = memberId(member);
              const checked = selected.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggle(id)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded border ${
                      checked
                        ? "border-orange-500 bg-orange-500 text-white"
                        : "border-gray-300"
                    }`}
                  >
                    {checked && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900">
                      {memberName(member)}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {roleLabel(member.role)}
                      {member.email ? ` · ${member.email}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-3 py-2">
            <span className="text-xs text-gray-500">
              {selectedMembers.length} selected
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg bg-gray-950 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ open, title, children, onClose, width = "max-w-2xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4">
      <div className={`max-h-[92vh] w-full ${width} overflow-y-auto rounded-2xl bg-white shadow-2xl`}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-950">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function WalkSchedulerView() {
  const { activeProject, projectRole, currentUser } = useProjectContext();
  const projectId = activeProject?.id;
  const currentUserId = Number(currentUser?.id);
  const calendarRef = useRef(null);

  const [project, setProject] = useState(activeProject || null);
  const [team, setTeam] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [busyPeriods, setBusyPeriods] = useState([]);
  const [teamBlocks, setTeamBlocks] = useState([]);
  const [oversightSchedules, setOversightSchedules] = useState([]);
  const [visibleRange, setVisibleRange] = useState(null);
  const [activeSection, setActiveSection] = useState("calendar");
  const [calendarView, setCalendarView] = useState("timeGridWeek");
  const [teamCalendarUserId, setTeamCalendarUserId] = useState(null);
  const [oversightUserId, setOversightUserId] = useState("all");
  const [oversightStatus, setOversightStatus] = useState("upcoming");
  const [availabilityPreview, setAvailabilityPreview] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [teamCalendarLoading, setTeamCalendarLoading] = useState(false);
  const [oversightLoading, setOversightLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [busyOpen, setBusyOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [requestForm, setRequestForm] = useState(emptyRequest);
  const [busyForm, setBusyForm] = useState(emptyBusy);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposal, setProposal] = useState({ date: "", start: "", end: "", note: "" });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const projectTimezone = project?.timezone || activeProject?.timezone || "UTC";
  const normalizedRole = String(projectRole || "").toLowerCase();
  const isSystemAdmin = String(currentUser?.role || "").toLowerCase() === "admin";
  const canUseOversight = isSystemAdmin || normalizedRole === "qa_qc_manager";

  const loadReferenceData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [projectResponse, teamResponse, inspectionResponse] = await Promise.all([
        projectsAPI.getOne(projectId),
        projectsAPI.getTeam(projectId),
        inspectionsAPI.listByProject(projectId).catch(() => ({ data: [] })),
      ]);
      setProject(projectResponse?.data || activeProject || null);
      setTeam(
        (Array.isArray(teamResponse?.data) ? teamResponse.data : []).filter(
          (member) => member.is_active !== false,
        ),
      );
      setInspections(
        Array.isArray(inspectionResponse?.data) ? inspectionResponse.data : [],
      );
    } catch (loadError) {
      setError(
        loadError?.response?.data?.detail ||
          loadError.message ||
          "Failed to load Walk Scheduler",
      );
    } finally {
      setLoading(false);
    }
  }, [activeProject, projectId]);

  const loadCalendar = useCallback(
    async (range = visibleRange) => {
      if (!projectId || !range?.start || !range?.end) return;
      setCalendarLoading(true);
      try {
        const response = await walkSchedulesAPI.listCalendar(
          projectId,
          range.start,
          range.end,
          "mine",
        );
        setSchedules(Array.isArray(response?.data?.schedules) ? response.data.schedules : []);
        setBusyPeriods(
          Array.isArray(response?.data?.busy_periods)
            ? response.data.busy_periods
            : [],
        );
      } catch (loadError) {
        setError(
          loadError?.response?.data?.detail ||
            loadError.message ||
            "Failed to load calendar",
        );
      } finally {
        setCalendarLoading(false);
      }
    },
    [projectId, visibleRange],
  );

  const loadTeamCalendar = useCallback(
    async (range = visibleRange, selectedUserId = teamCalendarUserId) => {
      if (!projectId || !selectedUserId || !range?.start || !range?.end) {
        setTeamBlocks([]);
        return;
      }
      setTeamCalendarLoading(true);
      try {
        const response = await walkSchedulesAPI.listTeamCalendar(
          projectId,
          selectedUserId,
          range.start,
          range.end,
        );
        setTeamBlocks(Array.isArray(response?.data?.blocks) ? response.data.blocks : []);
      } catch (loadError) {
        setError(
          loadError?.response?.data?.detail ||
            loadError.message ||
            "Failed to load team availability",
        );
      } finally {
        setTeamCalendarLoading(false);
      }
    },
    [projectId, teamCalendarUserId, visibleRange],
  );

  const loadOversight = useCallback(async () => {
    if (!projectId || !canUseOversight) return;
    setOversightLoading(true);
    try {
      const localNow = DateTime.now().setZone(projectTimezone);
      const rangeStart = localNow.minus({ days: 30 }).startOf("day").toUTC().toISO();
      const rangeEnd = localNow.plus({ days: 180 }).endOf("day").toUTC().toISO();
      const response = await walkSchedulesAPI.listCalendar(
        projectId,
        rangeStart,
        rangeEnd,
        "project",
      );
      setOversightSchedules(
        Array.isArray(response?.data?.schedules) ? response.data.schedules : [],
      );
    } catch (loadError) {
      setError(
        loadError?.response?.data?.detail ||
          loadError.message ||
          "Failed to load project appointments",
      );
    } finally {
      setOversightLoading(false);
    }
  }, [canUseOversight, projectId, projectTimezone]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    if (!visibleRange) return;
    if (activeSection === "calendar") loadCalendar(visibleRange);
    if (activeSection === "availability" && teamCalendarUserId) {
      loadTeamCalendar(visibleRange, teamCalendarUserId);
    }
  }, [
    activeSection,
    loadCalendar,
    loadTeamCalendar,
    teamCalendarUserId,
    visibleRange,
  ]);

  useEffect(() => {
    if (activeSection === "oversight" && canUseOversight) loadOversight();
  }, [activeSection, canUseOversight, loadOversight]);

  useEffect(() => {
    const handler = async (event) => {
      const scheduleId = Number(event?.detail?.walkScheduleId);
      if (!scheduleId || !projectId) return;
      let schedule = schedules.find((entry) => Number(entry.id) === scheduleId);
      if (!schedule) {
        try {
          const response = await walkSchedulesAPI.listByProject(projectId);
          schedule = (Array.isArray(response?.data) ? response.data : []).find(
            (entry) => Number(entry.id) === scheduleId,
          );
        } catch (openError) {
          console.error("Failed to open walk schedule", openError);
        }
      }
      if (schedule) {
        setSelectedSchedule(schedule);
        const start = DateTime.fromISO(schedule.preferred_start_at, { zone: "utc" });
        if (start.isValid) calendarRef.current?.getApi()?.gotoDate(start.toJSDate());
      }
    };
    window.addEventListener("smokyq:walk-schedule-open", handler);
    window.addEventListener("smokyq:notification-open", handler);
    return () => {
      window.removeEventListener("smokyq:walk-schedule-open", handler);
      window.removeEventListener("smokyq:notification-open", handler);
    };
  }, [projectId, schedules]);

  const availablePeople = useMemo(
    () => team.filter((member) => memberId(member) !== currentUserId).sort(compareMembers),
    [currentUserId, team],
  );

  useEffect(() => {
    const selectedStillExists = availablePeople.some(
      (member) => memberId(member) === Number(teamCalendarUserId),
    );
    if (!selectedStillExists) {
      setTeamCalendarUserId(
        availablePeople.length ? memberId(availablePeople[0]) : null,
      );
    }
  }, [availablePeople, teamCalendarUserId]);

  const ccOptions = useMemo(() => {
    const requested = new Set(requestForm.requested_user_ids.map(Number));
    return availablePeople.filter((member) => !requested.has(memberId(member)));
  }, [availablePeople, requestForm.requested_user_ids]);

  const selectedRequestedMembers = useMemo(() => {
    const selected = new Set(requestForm.requested_user_ids.map(Number));
    return availablePeople.filter((member) => selected.has(memberId(member)));
  }, [availablePeople, requestForm.requested_user_ids]);

  const calendarEvents = useMemo(() => {
    if (activeSection === "availability") {
      return teamBlocks.map((block, index) => ({
        id: `${block.block_type}-${block.schedule_id || block.busy_period_id || index}-${block.start_at}`,
        title: block.title,
        start: block.start_at,
        end: block.end_at,
        backgroundColor:
          block.block_type === "busy" ? "#fee2e2" : scheduleColor(block.status),
        borderColor:
          block.block_type === "busy" ? "#ef4444" : scheduleColor(block.status),
        textColor: block.block_type === "busy" ? "#991b1b" : "#ffffff",
        extendedProps: { kind: "team-block", block },
      }));
    }

    const scheduleEvents = schedules.map((schedule) => {
      const myResponse = participantResponse(schedule, currentUserId);
      const displayStatus =
        !["accepted", "cancelled", "rejected"].includes(
          String(schedule.status || "").toLowerCase(),
        ) && myResponse?.response_status
          ? myResponse.response_status
          : schedule.status;
      const summary = responseSummary(schedule);
      const progressSuffix =
        summary.total > 1 && schedule.status !== "accepted"
          ? ` · ${summary.accepted}/${summary.total} accepted`
          : "";
      return {
        id: `schedule-${schedule.id}`,
        title: `${schedule.title}${progressSuffix}`,
        start: schedule.preferred_start_at,
        end: schedule.preferred_end_at,
        backgroundColor: scheduleColor(displayStatus),
        borderColor: scheduleColor(displayStatus),
        extendedProps: { kind: "schedule", schedule },
      };
    });
    const busyEvents = busyPeriods.map((busy, index) => ({
      id: `busy-${busy.id}-${busy.occurrence_date || index}`,
      title: busy.note || "Busy — Not available",
      start: busy.start_at,
      end: busy.end_at,
      backgroundColor: "#fee2e2",
      borderColor: "#ef4444",
      textColor: "#991b1b",
      extendedProps: { kind: "busy", busy },
    }));
    return [...scheduleEvents, ...busyEvents];
  }, [activeSection, busyPeriods, currentUserId, schedules, teamBlocks]);

  const nextUpcomingEventId = useMemo(() => {
    const now = DateTime.now().toMillis();
    const upcoming = calendarEvents
      .filter((event) => {
        const status = String(
          event.extendedProps?.schedule?.status ||
            event.extendedProps?.block?.status ||
            "",
        ).toLowerCase();
        const start = DateTime.fromISO(event.start || "", { zone: "utc" });
        return (
          start.isValid &&
          start.toMillis() >= now &&
          !["cancelled", "rejected"].includes(status)
        );
      })
      .sort(
        (left, right) =>
          DateTime.fromISO(left.start, { zone: "utc" }).toMillis() -
          DateTime.fromISO(right.start, { zone: "utc" }).toMillis(),
      );
    return upcoming[0]?.id || null;
  }, [calendarEvents]);

  const displayedCalendarEvents = useMemo(() => {
    if (calendarView !== "listMonth") return calendarEvents;
    const now = DateTime.now().toMillis();
    return calendarEvents
      .filter((event) => {
        const end = DateTime.fromISO(event.end || event.start || "", {
          zone: "utc",
        });
        return end.isValid && end.toMillis() >= now;
      })
      .sort(
        (left, right) =>
          DateTime.fromISO(left.start, { zone: "utc" }).toMillis() -
          DateTime.fromISO(right.start, { zone: "utc" }).toMillis(),
      );
  }, [calendarEvents, calendarView]);


  const businessHours = useMemo(() => {
    const days = Array.isArray(project?.working_days)
      ? project.working_days.map((day) => (Number(day) + 1) % 7)
      : [1, 2, 3, 4, 5];
    return {
      daysOfWeek: days,
      startTime: project?.workday_start || "08:00",
      endTime: project?.workday_end || "17:00",
    };
  }, [project]);

  const openRequestForSelection = (selection) => {
    setError(null);
    setMessage(null);
    const start = DateTime.fromISO(selection.startStr).setZone(projectTimezone);
    const end = DateTime.fromISO(selection.endStr).setZone(projectTimezone);
    setRequestForm({
      ...emptyRequest,
      requested_user_ids:
        activeSection === "availability" && teamCalendarUserId
          ? [Number(teamCalendarUserId)]
          : [],
      schedule_date: start.toISODate(),
      start_time: start.toFormat("HH:mm"),
      end_time: end.toFormat("HH:mm"),
    });
    setRequestOpen(true);
  };

  const checkAvailability = useCallback(
    async ({ startIso, endIso, userIds, ccIds, excludeScheduleId }) => {
      const response = await walkSchedulesAPI.checkAvailability(projectId, {
        user_ids: userIds,
        cc_user_ids: ccIds,
        start_at: startIso,
        end_at: endIso,
        exclude_schedule_id: excludeScheduleId || null,
      });
      return response?.data || {
        available: true,
        hard_conflicts: [],
        warnings: [],
      };
    },
    [projectId],
  );

  const formatConflictWindow = (item) => {
    const start = DateTime.fromISO(item?.start_at || "", { zone: "utc" }).setZone(
      projectTimezone,
    );
    const end = DateTime.fromISO(item?.end_at || "", { zone: "utc" }).setZone(
      projectTimezone,
    );
    if (!start.isValid) return "Time unavailable";
    return `${start.toFormat("ccc, LLL d · h:mm a")} – ${
      end.isValid ? end.toFormat("h:mm a") : "—"
    }`;
  };

  const conflictText = (items) =>
    (items || [])
      .map(
        (item) =>
          `${item.user_name || `User #${item.user_id}`}: ${item.label} (${formatConflictWindow(item)})`,
      )
      .join("\n");

  const openTeamCalendar = (userId, dateValue = requestForm.schedule_date) => {
    if (!userId) return;
    setTeamCalendarUserId(Number(userId));
    setActiveSection("availability");
    setRequestOpen(false);
    window.setTimeout(() => {
      const api = calendarRef.current?.getApi?.();
      if (api && dateValue) api.gotoDate(dateValue);
    }, 60);
  };

  const changeCalendarView = (viewId) => {
    calendarRef.current?.getApi?.().changeView(viewId);
    setCalendarView(viewId);
  };

  useEffect(() => {
    if (!requestOpen) {
      setAvailabilityPreview(null);
      setAvailabilityLoading(false);
      return undefined;
    }
    const startIso = toUtcIso(
      requestForm.schedule_date,
      requestForm.start_time,
      projectTimezone,
    );
    const endIso = toUtcIso(
      requestForm.schedule_date,
      requestForm.end_time,
      projectTimezone,
    );
    if (
      !requestForm.requested_user_ids.length ||
      !startIso ||
      !endIso ||
      DateTime.fromISO(endIso) <= DateTime.fromISO(startIso)
    ) {
      setAvailabilityPreview(null);
      setAvailabilityLoading(false);
      return undefined;
    }

    setAvailabilityPreview(null);
    setAvailabilityLoading(true);
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkAvailability({
          startIso,
          endIso,
          userIds: [currentUserId, ...requestForm.requested_user_ids],
          ccIds: requestForm.cc_user_ids,
        });
        if (!cancelled) setAvailabilityPreview(result);
      } catch (previewError) {
        if (!cancelled) {
          setAvailabilityPreview({
            available: false,
            hard_conflicts: [],
            warnings: [],
            preview_error:
              previewError?.response?.data?.detail ||
              previewError.message ||
              "Availability preview could not be loaded.",
          });
        }
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    checkAvailability,
    currentUserId,
    projectTimezone,
    requestForm.cc_user_ids,
    requestForm.end_time,
    requestForm.requested_user_ids,
    requestForm.schedule_date,
    requestForm.start_time,
    requestOpen,
  ]);

  const oversightFiltered = useMemo(() => {
    const now = DateTime.now().toMillis();
    return oversightSchedules
      .filter((schedule) => {
        const participantIds = new Set([
          Number(schedule.requested_by_user_id),
          ...(schedule.requested_user_ids || []).map(Number),
          ...(schedule.cc_user_ids || []).map(Number),
        ]);
        if (
          oversightUserId !== "all" &&
          !participantIds.has(Number(oversightUserId))
        ) {
          return false;
        }
        const status = String(schedule.status || "requested").toLowerCase();
        const start = DateTime.fromISO(schedule.preferred_start_at || "", {
          zone: "utc",
        });
        const isUpcoming = start.isValid && start.toMillis() >= now;
        if (oversightStatus === "upcoming") {
          return isUpcoming && !["cancelled", "rejected"].includes(status);
        }
        if (oversightStatus === "all") return true;
        return status === oversightStatus;
      })
      .sort((left, right) => {
        const leftTime = DateTime.fromISO(left.preferred_start_at || "", {
          zone: "utc",
        }).toMillis();
        const rightTime = DateTime.fromISO(right.preferred_start_at || "", {
          zone: "utc",
        }).toMillis();
        return oversightStatus === "upcoming"
          ? leftTime - rightTime
          : rightTime - leftTime;
      });
  }, [oversightSchedules, oversightStatus, oversightUserId]);

  const selectedResponseSummary = useMemo(
    () => responseSummary(selectedSchedule),
    [selectedSchedule],
  );
  const selectedCurrentParticipant = useMemo(
    () => participantResponse(selectedSchedule, currentUserId),
    [currentUserId, selectedSchedule],
  );
  const canCurrentUserRespond = Boolean(selectedCurrentParticipant) &&
    !["accepted", "rejected"].includes(
      String(selectedCurrentParticipant?.response_status || "requested").toLowerCase(),
    ) &&
    !["accepted", "rejected", "cancelled"].includes(
      String(selectedSchedule?.status || "").toLowerCase(),
    );
  const canCurrentUserEdit =
    Boolean(selectedSchedule) &&
    (isSystemAdmin ||
      Number(selectedSchedule?.requested_by_user_id) === currentUserId) &&
    String(selectedSchedule?.status || "").toLowerCase() !== "cancelled";

  const submitRequest = async (event) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    const startIso = toUtcIso(
      requestForm.schedule_date,
      requestForm.start_time,
      projectTimezone,
    );
    const endIso = toUtcIso(
      requestForm.schedule_date,
      requestForm.end_time,
      projectTimezone,
    );
    if (!startIso || !endIso || DateTime.fromISO(endIso) <= DateTime.fromISO(startIso)) {
      setError("Choose a valid date and From/To time.");
      return;
    }
    if (!requestForm.requested_user_ids.length) {
      setError("Select at least one requested participant.");
      return;
    }

    setSubmitting(true);
    try {
      const availability = await checkAvailability({
        startIso,
        endIso,
        userIds: [currentUserId, ...requestForm.requested_user_ids],
        ccIds: requestForm.cc_user_ids,
      });
      if (availability.hard_conflicts?.length) {
        setError(
          `This time is unavailable:\n${conflictText(availability.hard_conflicts)}`,
        );
        return;
      }
      if (
        availability.warnings?.length &&
        !window.confirm(
          `Some participants have pending schedule conflicts:\n\n${conflictText(
            availability.warnings,
          )}\n\nCreate the request anyway?`,
        )
      ) {
        return;
      }

      const response = await walkSchedulesAPI.create(projectId, {
        requested_user_ids: requestForm.requested_user_ids.map(Number),
        cc_user_ids: requestForm.cc_user_ids.map(Number),
        inspection_id: requestForm.inspection_id
          ? Number(requestForm.inspection_id)
          : null,
        meeting_type: requestForm.meeting_type,
        title: requestForm.title.trim(),
        location: requestForm.location.trim() || null,
        notes: requestForm.notes.trim() || null,
        preferred_start_at: startIso,
        preferred_end_at: endIso,
      });
      setMessage(
        response?.data?.email_warning || "Meeting request created and invitations sent.",
      );
      setRequestOpen(false);
      setRequestForm(emptyRequest);
      await loadCalendar();
    } catch (submitError) {
      const detail = submitError?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || submitError.message || "Failed to create request",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitBusy = async (event) => {
    event.preventDefault();
    setError(null);
    const startIso = toUtcIso(
      busyForm.schedule_date,
      busyForm.start_time,
      projectTimezone,
    );
    const endIso = toUtcIso(
      busyForm.schedule_date,
      busyForm.end_time,
      projectTimezone,
    );
    if (!startIso || !endIso || DateTime.fromISO(endIso) <= DateTime.fromISO(startIso)) {
      setError("Choose a valid busy period.");
      return;
    }
    if (busyForm.recurrence_type !== "none" && !busyForm.repeat_until) {
      setError("Choose the repeat-until date.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        start_at: startIso,
        end_at: endIso,
        recurrence_type: busyForm.recurrence_type,
        repeat_until:
          busyForm.recurrence_type === "none" ? null : busyForm.repeat_until,
        note: busyForm.note.trim() || null,
      };
      if (busyForm.id) {
        await walkSchedulesAPI.updateBusyPeriod(projectId, busyForm.id, payload);
      } else {
        await walkSchedulesAPI.createBusyPeriod(projectId, payload);
      }
      setMessage(busyForm.id ? "Busy period updated." : "Busy period added.");
      setBusyOpen(false);
      setBusyForm(emptyBusy);
      await loadCalendar();
    } catch (busyError) {
      setError(
        busyError?.response?.data?.detail || busyError.message || "Failed to save busy period",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const deleteBusy = async () => {
    if (!busyForm.id || !window.confirm("Delete this entire busy-period pattern?")) return;
    setSubmitting(true);
    try {
      await walkSchedulesAPI.deleteBusyPeriod(projectId, busyForm.id);
      setBusyOpen(false);
      setBusyForm(emptyBusy);
      setMessage("Busy period deleted.");
      await loadCalendar();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.detail || "Failed to delete busy period");
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (status, extra = {}) => {
    if (!selectedSchedule) return;
    setStatusLoading(true);
    setError(null);
    try {
      const response = await walkSchedulesAPI.updateStatus(
        projectId,
        selectedSchedule.id,
        { status, ...extra },
      );
      setSelectedSchedule(response?.data || null);
      setMessage(response?.data?.email_warning || `Meeting ${status.replaceAll("_", " ")}.`);
      await loadCalendar();
    } catch (statusError) {
      const detail = statusError?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || statusError.message || "Failed to update meeting",
      );
    } finally {
      setStatusLoading(false);
    }
  };

  const submitProposal = async (event) => {
    event.preventDefault();
    const startIso = toUtcIso(proposal.date, proposal.start, projectTimezone);
    const endIso = toUtcIso(proposal.date, proposal.end, projectTimezone);
    if (!startIso || !endIso || DateTime.fromISO(endIso) <= DateTime.fromISO(startIso)) {
      setError("Choose a valid proposed date and time.");
      return;
    }
    setStatusLoading(true);
    try {
      const primaryIds = [selectedSchedule.requested_by_user_id].concat(
        selectedSchedule.requested_user_ids || [],
      );
      const availability = await checkAvailability({
        startIso,
        endIso,
        userIds: primaryIds,
        ccIds: selectedSchedule.cc_user_ids || [],
        excludeScheduleId: selectedSchedule.id,
      });
      if (availability.hard_conflicts?.length) {
        setError(`The proposed time is unavailable:\n${conflictText(availability.hard_conflicts)}`);
        return;
      }
      await updateStatus("proposed_new_time", {
        preferred_start_at: startIso,
        preferred_end_at: endIso,
        response_note: proposal.note.trim() || null,
      });
      setProposalOpen(false);
    } finally {
      setStatusLoading(false);
    }
  };

  const openBusyFromEvent = (busy) => {
    const start = localFields(busy.series_start_at || busy.start_at, projectTimezone);
    const end = localFields(busy.series_end_at || busy.end_at, projectTimezone);
    setBusyForm({
      id: busy.id,
      schedule_date: start.date,
      start_time: start.time,
      end_time: end.time,
      recurrence_type: busy.recurrence_type || "none",
      repeat_until: busy.repeat_until || "",
      note: busy.note || "",
    });
    setBusyOpen(true);
  };

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-gray-950 to-gray-800 p-5 text-white shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-orange-300">
            <CalendarClock className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em]">
              Project calendar
            </span>
          </div>
          <h2 className="mt-1 text-2xl font-semibold">Walk Scheduler</h2>
          <p className="mt-1 text-sm text-gray-300">
            All times use <b>{projectTimezone}</b>. Accepted walks receive a one-hour email reminder.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              if (activeSection === "oversight") loadOversight();
              else if (activeSection === "availability") loadTeamCalendar();
              else loadCalendar();
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm font-medium hover:bg-white/15"
          >
            <RefreshCw
              className={`h-4 w-4 ${
                calendarLoading || teamCalendarLoading || oversightLoading
                  ? "animate-spin"
                  : ""
              }`}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setMessage(null);
              setBusyForm(emptyBusy);
              setBusyOpen(true);
            }}
            title="Block a time when you are unavailable; your private note is not shown to other users."
            className="inline-flex items-center gap-2 rounded-xl border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm font-medium text-red-100 hover:bg-red-500/25"
          >
            <Clock3 className="h-4 w-4" />
            Add busy time
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              setMessage(null);
              setRequestForm(emptyRequest);
              setRequestOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
          >
            <Plus className="h-4 w-4" />
            New request
          </button>
        </div>
      </div>

      {(error || message) && (
        <div
          className={`whitespace-pre-line rounded-xl border px-4 py-3 text-sm ${
            error
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          {error || message}
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              id: "calendar",
              label: "My calendar",
              description: "Your walks and personal busy time",
              icon: UserRound,
            },
            {
              id: "availability",
              label: "Team availability",
              description: "Check one person before requesting",
              icon: Users,
            },
            ...(canUseOversight
              ? [
                  {
                    id: "oversight",
                    label: "Appointment oversight",
                    description: "Admin / QA-QC project appointment list",
                    icon: ShieldCheck,
                  },
                ]
              : []),
          ].map((section) => {
            const Icon = section.icon;
            const active = activeSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                  active
                    ? "border-orange-400 bg-orange-50 text-orange-900 shadow-sm"
                    : "border-transparent bg-gray-50 text-gray-700 hover:border-gray-200 hover:bg-white"
                }`}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                    active ? "bg-orange-500 text-white" : "bg-white text-gray-500"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{section.label}</span>
                  <span className="block truncate text-xs opacity-70">
                    {section.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {activeSection !== "oversight" ? (
        <>
          {activeSection === "availability" && (
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-950">
                    <Eye className="h-4 w-4" />
                    View one team member at a time
                  </div>
                  <p className="mt-1 text-xs leading-5 text-blue-800">
                    Exact unavailable hours are visible so you can choose a free slot.
                    Private busy notes remain hidden unless the calendar is your own.
                  </p>
                </div>
                <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                  <label className="min-w-64 text-xs font-semibold text-blue-950">
                    Team member
                    <select
                      value={teamCalendarUserId || ""}
                      onChange={(event) =>
                        setTeamCalendarUserId(Number(event.target.value))
                      }
                      className="mt-1 w-full rounded-xl border border-blue-200 bg-white px-3 py-2 text-sm text-gray-900"
                    >
                      {availablePeople.map((member) => (
                        <option key={memberId(member)} value={memberId(member)}>
                          {memberName(member)} · {roleLabel(member.role)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {(requestForm.requested_user_ids.length > 0 ||
                    requestForm.schedule_date ||
                    requestForm.title) && (
                    <button
                      type="button"
                      onClick={() => setRequestOpen(true)}
                      className="self-end rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
                    >
                      Continue request
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {CALENDAR_VIEWS.map((view) => {
              const Icon = view.icon;
              const active = calendarView === view.id;
              return (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => changeCalendarView(view.id)}
                  className={`group flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                    active
                      ? "border-gray-950 bg-gray-950 text-white shadow-md"
                      : "border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                      active
                        ? "bg-white/15 text-orange-300"
                        : "bg-gray-100 text-gray-500 group-hover:bg-white"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">{view.label}</span>
                    <span className={`block text-xs ${active ? "text-gray-300" : "text-gray-500"}`}>
                      {view.hint}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {calendarView === "listMonth" && (
            <div className="flex flex-col gap-2 rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-sm text-violet-900 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <List className="h-4 w-4" />
                <span>
                  Agenda is ordered by time, with the nearest upcoming appointment first.
                  Past entries are muted and the next appointment is highlighted.
                </span>
              </div>
              {nextUpcomingEventId && (
                <span className="shrink-0 rounded-full bg-violet-700 px-3 py-1 text-xs font-semibold text-white">
                  Next up highlighted
                </span>
              )}
            </div>
          )}

          <div className="relative rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
            {(calendarLoading || teamCalendarLoading) && (
              <div className="absolute right-5 top-5 z-20 rounded-full bg-white p-2 shadow">
                <RefreshCw className="h-4 w-4 animate-spin text-orange-500" />
              </div>
            )}
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                interactionPlugin,
                luxonPlugin,
              ]}
              initialView="timeGridWeek"
              timeZone={projectTimezone}
              events={displayedCalendarEvents}
              selectable
              selectMirror
              nowIndicator
              allDaySlot={false}
              businessHours={businessHours}
              slotMinTime="06:00:00"
              slotMaxTime="20:00:00"
              slotDuration="00:30:00"
              height="auto"
              firstDay={1}
              eventOrder="start,title"
              eventOrderStrict
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "",
              }}
              buttonText={{ today: "Today" }}
              views={{
                timeGridWeek: { weekends: false },
                listMonth: { noEventsContent: "No upcoming appointments in this month" },
              }}
              datesSet={(info) => {
                setVisibleRange({ start: info.startStr, end: info.endStr });
                setCalendarView(info.view.type);
              }}
              select={openRequestForSelection}
              eventContent={(info) => {
                const isNext = info.event.id === nextUpcomingEventId;
                const status = String(
                  info.event.extendedProps?.schedule?.status ||
                    info.event.extendedProps?.block?.status ||
                    "",
                ).toLowerCase();
                if (calendarView !== "listMonth") {
                  return (
                    <div className="min-w-0 truncate">
                      {info.timeText && (
                        <span className="mr-1 text-[10px] font-semibold opacity-80">
                          {info.timeText}
                        </span>
                      )}
                      <span className="truncate">{info.event.title}</span>
                    </div>
                  );
                }
                return (
                  <div className="flex min-w-0 flex-wrap items-center gap-2 py-0.5">
                    <span className="min-w-0 truncate font-medium">
                      {info.event.title}
                    </span>
                    <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700 ring-1 ring-gray-200">
                      {relativeDateLabel(
                        info.event.start?.toISOString(),
                        projectTimezone,
                      )}
                    </span>
                    {isNext && (
                      <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Next up
                      </span>
                    )}
                    {status === "cancelled" && (
                      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-700">
                        Cancelled
                      </span>
                    )}
                  </div>
                );
              }}
              eventClick={async (info) => {
                const { kind, schedule, busy, block } = info.event.extendedProps;
                if (kind === "schedule") setSelectedSchedule(schedule);
                if (kind === "busy") openBusyFromEvent(busy);
                if (kind === "team-block" && block?.schedule_id) {
                  const knownSchedule = [...schedules, ...oversightSchedules].find(
                    (entry) => Number(entry.id) === Number(block.schedule_id),
                  );
                  if (knownSchedule) {
                    setSelectedSchedule(knownSchedule);
                  } else {
                    try {
                      const response = await walkSchedulesAPI.listByProject(projectId);
                      const found = (Array.isArray(response?.data) ? response.data : []).find(
                        (entry) => Number(entry.id) === Number(block.schedule_id),
                      );
                      if (found) setSelectedSchedule(found);
                    } catch (openError) {
                      setError(
                        openError?.response?.data?.detail ||
                          openError.message ||
                          "Failed to open appointment",
                      );
                    }
                  }
                }
              }}
              eventDidMount={(info) => {
                info.el.title = `${info.event.title} · ${relativeDateLabel(
                  info.event.start?.toISOString(),
                  projectTimezone,
                )}`;
                const eventEnd = info.event.end || info.event.start;
                if (eventEnd && eventEnd.getTime() < Date.now()) {
                  info.el.style.opacity = "0.58";
                }
                if (info.event.id === nextUpcomingEventId) {
                  info.el.style.boxShadow = "0 0 0 2px rgba(124, 58, 237, 0.45)";
                }
              }}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {[
              ["Requested", "bg-orange-500"],
              ["Accepted", "bg-emerald-600"],
              ["Proposed", "bg-blue-600"],
              ["Busy", "bg-red-300"],
              ["Rejected", "bg-red-600"],
              ["Cancelled", "bg-gray-500"],
            ].map(([label, color]) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600"
              >
                <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
                {label}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-950">
                <ShieldCheck className="h-5 w-5 text-orange-500" />
                Project appointment oversight
              </div>
              <p className="mt-1 max-w-2xl text-xs leading-5 text-gray-500">
                Admin and QA/QC can review project appointments here without placing every
                person on the main calendar. Filter by person or status and open only the
                appointment that needs attention.
              </p>
            </div>
            <div className="grid w-full gap-2 sm:grid-cols-2 lg:w-auto">
              <label className="text-xs font-semibold text-gray-700">
                Person
                <select
                  value={oversightUserId}
                  onChange={(event) => setOversightUserId(event.target.value)}
                  className="mt-1 min-w-56 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-900"
                >
                  <option value="all">All project members</option>
                  {team.map((member) => (
                    <option key={memberId(member)} value={memberId(member)}>
                      {memberName(member)} · {roleLabel(member.role)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-gray-700">
                View
                <select
                  value={oversightStatus}
                  onChange={(event) => setOversightStatus(event.target.value)}
                  className="mt-1 min-w-44 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-900"
                >
                  <option value="upcoming">Upcoming active</option>
                  <option value="all">All recent</option>
                  <option value="requested">Requested</option>
                  <option value="accepted">Accepted</option>
                  <option value="proposed_new_time">Proposed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rejected">Rejected</option>
                </select>
              </label>
            </div>
          </div>

          {oversightLoading ? (
            <div className="flex min-h-48 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : oversightFiltered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 px-5 py-12 text-center">
              <CalendarClock className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-2 text-sm font-medium text-gray-700">
                No appointments match these filters.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {oversightFiltered.map((schedule, index) => {
                const status = String(schedule.status || "requested").toLowerCase();
                const start = DateTime.fromISO(schedule.preferred_start_at || "", {
                  zone: "utc",
                }).setZone(projectTimezone);
                const isUpcoming = start.isValid && start.toMillis() >= Date.now();
                const participantNames = (schedule.requested_users || [])
                  .map((person) => person.name)
                  .filter(Boolean)
                  .join(", ");
                return (
                  <button
                    key={schedule.id}
                    type="button"
                    onClick={() => setSelectedSchedule(schedule)}
                    className={`flex w-full flex-col gap-3 rounded-xl border p-4 text-left transition sm:flex-row sm:items-center ${
                      status === "cancelled"
                        ? "border-gray-200 bg-gray-50 opacity-75 hover:opacity-100"
                        : index === 0 && oversightStatus === "upcoming"
                          ? "border-violet-200 bg-violet-50 hover:border-violet-300"
                          : "border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/30"
                    }`}
                  >
                    <div className="flex min-w-24 shrink-0 items-center gap-2 sm:block">
                      <div className="text-xs font-bold uppercase tracking-wide text-gray-500">
                        {relativeDateLabel(schedule.preferred_start_at, projectTimezone)}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-950">
                        {start.isValid ? start.toFormat("h:mm a") : "—"}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-semibold text-gray-950">
                          {schedule.title}
                        </span>
                        {index === 0 && oversightStatus === "upcoming" && isUpcoming && (
                          <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                            Next up
                          </span>
                        )}
                      </div>
                      <div className="mt-1 truncate text-xs text-gray-500">
                        Requested by {schedule.requested_by_name || "—"}
                        {participantNames ? ` · With ${participantNames}` : ""}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span
                        className="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize text-white"
                        style={{ backgroundColor: scheduleColor(status) }}
                      >
                        {status.replaceAll("_", " ")}
                      </span>
                      <Eye className="h-4 w-4 text-gray-400" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Modal open={requestOpen} title="New walk or meeting request" onClose={() => setRequestOpen(false)}>
        <form onSubmit={submitRequest} className="space-y-4 p-5">
          {error && (
            <div className="whitespace-pre-line rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Requested participants *
              <div className="mt-1.5">
                <MultiPersonSelect
                  options={availablePeople}
                  selectedIds={requestForm.requested_user_ids}
                  onChange={(value) =>
                    setRequestForm((current) => ({
                      ...current,
                      requested_user_ids: value,
                      cc_user_ids: current.cc_user_ids.filter(
                        (id) => !value.map(Number).includes(Number(id)),
                      ),
                    }))
                  }
                  placeholder="Select one or more participants"
                />
              </div>
            </label>
            <label className="text-sm font-medium text-gray-700">
              CC
              <div className="mt-1.5">
                <MultiPersonSelect
                  options={ccOptions}
                  selectedIds={requestForm.cc_user_ids}
                  onChange={(value) =>
                    setRequestForm((current) => ({ ...current, cc_user_ids: value }))
                  }
                  placeholder="Optional copied users"
                />
              </div>
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Title *
              <input
                required
                value={requestForm.title}
                onChange={(event) =>
                  setRequestForm((current) => ({ ...current, title: event.target.value }))
                }
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Foundation site walk"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Meeting type
              <select
                value={requestForm.meeting_type}
                onChange={(event) =>
                  setRequestForm((current) => ({ ...current, meeting_type: event.target.value }))
                }
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5"
              >
                {MEETING_TYPES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm font-medium text-gray-700">
              Date *
              <input
                required
                type="date"
                value={requestForm.schedule_date}
                onChange={(event) =>
                  setRequestForm((current) => ({ ...current, schedule_date: event.target.value }))
                }
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              From *
              <input
                required
                type="time"
                value={requestForm.start_time}
                onChange={(event) =>
                  setRequestForm((current) => ({ ...current, start_time: event.target.value }))
                }
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              To *
              <input
                required
                type="time"
                value={requestForm.end_time}
                onChange={(event) =>
                  setRequestForm((current) => ({ ...current, end_time: event.target.value }))
                }
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5"
              />
            </label>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-950">
                  <Users className="h-4 w-4 text-orange-500" />
                  Participant availability
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Exact unavailable hours appear here before you send the request.
                </p>
              </div>
              {selectedRequestedMembers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedRequestedMembers.map((member) => (
                    <button
                      key={memberId(member)}
                      type="button"
                      onClick={() => openTeamCalendar(memberId(member))}
                      className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View {memberName(member).split(" ")[0]}'s calendar
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3">
              {availabilityLoading ? (
                <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs text-gray-600">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin text-orange-500" />
                  Checking selected calendars…
                </div>
              ) : !requestForm.requested_user_ids.length ||
                !requestForm.schedule_date ||
                !requestForm.start_time ||
                !requestForm.end_time ? (
                <div className="flex items-start gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-xs text-gray-600">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Select participants, date, From and To to preview availability.
                </div>
              ) : availabilityPreview?.preview_error ? (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {String(availabilityPreview.preview_error)}
                </div>
              ) : availabilityPreview?.hard_conflicts?.length ||
                availabilityPreview?.warnings?.length ? (
                <div className="space-y-2">
                  {(availabilityPreview.hard_conflicts || []).map((item, index) => (
                    <div
                      key={`hard-${item.user_id}-${item.schedule_id || item.busy_period_id || index}`}
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2"
                    >
                      <div className="flex items-center gap-2 text-xs font-semibold text-red-900">
                        <XCircle className="h-3.5 w-3.5" />
                        {item.user_name || `User #${item.user_id}`} is unavailable
                      </div>
                      <div className="mt-1 text-xs text-red-800">
                        {formatConflictWindow(item)} · {item.label}
                      </div>
                    </div>
                  ))}
                  {(availabilityPreview.warnings || []).map((item, index) => (
                    <div
                      key={`warning-${item.user_id}-${item.schedule_id || item.busy_period_id || index}`}
                      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                    >
                      <div className="flex items-center gap-2 text-xs font-semibold text-amber-900">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Possible conflict for {item.user_name || `User #${item.user_id}`}
                      </div>
                      <div className="mt-1 text-xs text-amber-800">
                        {formatConflictWindow(item)} · {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              ) : availabilityPreview ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" />
                  The requester and selected participants are available in this time window.
                </div>
              ) : null}
            </div>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            Location <span className="font-normal text-gray-400">(optional)</span>
            <input
              value={requestForm.location}
              onChange={(event) =>
                setRequestForm((current) => ({
                  ...current,
                  location: event.target.value,
                }))
              }
              maxLength={500}
              placeholder="Conference room, trailer, building/level, or online link"
              className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5"
            />
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Related inspection
            <select
              value={requestForm.inspection_id}
              onChange={(event) =>
                setRequestForm((current) => ({ ...current, inspection_id: event.target.value }))
              }
              className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5"
            >
              <option value="">No inspection selected</option>
              {inspections.map((inspection) => (
                <option key={inspection.id} value={inspection.id}>
                  {inspection.inspection_number || `Inspection #${inspection.id}`}
                  {inspection.site_location ? ` · ${inspection.site_location}` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Notes
            <textarea
              rows={3}
              value={requestForm.notes}
              onChange={(event) =>
                setRequestForm((current) => ({ ...current, notes: event.target.value }))
              }
              className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5"
              placeholder="Access instructions, agenda, or preparation notes"
            />
          </label>

          <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs leading-5 text-blue-800">
            Accepted walks and personal busy periods block the time. Pending requests produce a warning. Other users can see the unavailable hours, but personal busy notes remain private.
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button type="button" onClick={() => setRequestOpen(false)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium">
              Cancel
            </button>
            <button
              disabled={
                submitting ||
                availabilityLoading ||
                Boolean(availabilityPreview?.hard_conflicts?.length)
              }
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? <LoadingSpinner size="small" /> : <Send className="h-4 w-4" />}
              Send request
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={busyOpen} title={busyForm.id ? "Edit busy time" : "Add personal busy time"} onClose={() => setBusyOpen(false)} width="max-w-xl">
        <form onSubmit={submitBusy} className="space-y-4 p-5">
          <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm leading-5 text-red-800">
            <div className="flex items-center gap-2 font-semibold">
              <Info className="h-4 w-4" />
              What busy time does
            </div>
            <p className="mt-1 text-xs">
              It blocks this time from new walk requests. Use one-time, daily, or weekly repeat options. Other users see the exact unavailable hours as “Busy — Not available,” while your private note is visible only to you.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm font-medium text-gray-700">
              Date *
              <input required type="date" value={busyForm.schedule_date} onChange={(event) => setBusyForm((current) => ({ ...current, schedule_date: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" />
            </label>
            <label className="text-sm font-medium text-gray-700">
              From *
              <input required type="time" value={busyForm.start_time} onChange={(event) => setBusyForm((current) => ({ ...current, start_time: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" />
            </label>
            <label className="text-sm font-medium text-gray-700">
              To *
              <input required type="time" value={busyForm.end_time} onChange={(event) => setBusyForm((current) => ({ ...current, end_time: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" />
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Repeat
              <select value={busyForm.recurrence_type} onChange={(event) => setBusyForm((current) => ({ ...current, recurrence_type: event.target.value, repeat_until: event.target.value === "none" ? "" : current.repeat_until }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5">
                <option value="none">Does not repeat</option>
                <option value="daily">Daily until date</option>
                <option value="weekly">Weekly until date</option>
              </select>
            </label>
            {busyForm.recurrence_type !== "none" && (
              <label className="text-sm font-medium text-gray-700">
                Repeat until *
                <input required type="date" value={busyForm.repeat_until} onChange={(event) => setBusyForm((current) => ({ ...current, repeat_until: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" />
              </label>
            )}
          </div>
          {busyForm.recurrence_type !== "none" && busyForm.schedule_date && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setBusyForm((current) => ({ ...current, repeat_until: DateTime.fromISO(current.schedule_date).endOf("week").toISODate() }))} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium hover:bg-gray-100">
                Through this week
              </button>
              <button type="button" onClick={() => setBusyForm((current) => ({ ...current, repeat_until: DateTime.fromISO(current.schedule_date).endOf("month").toISODate() }))} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium hover:bg-gray-100">
                Through this month
              </button>
            </div>
          )}
          <label className="block text-sm font-medium text-gray-700">
            Private note
            <input value={busyForm.note} onChange={(event) => setBusyForm((current) => ({ ...current, note: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" placeholder="Training, client call, site travel…" />
          </label>
          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <div>
              {busyForm.id && (
                <button type="button" onClick={deleteBusy} disabled={submitting} className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" /> Delete pattern
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setBusyOpen(false)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium">Cancel</button>
              <button disabled={submitting} className="rounded-xl bg-gray-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Save busy time</button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(selectedSchedule)}
        title="Schedule details"
        onClose={() => {
          setEditOpen(false);
          setSelectedSchedule(null);
        }}
        width="max-w-xl"
      >
        {selectedSchedule && (
          <div className="space-y-4 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-xl font-semibold text-gray-950">{selectedSchedule.title}</h4>
                <p className="mt-1 text-sm text-gray-500">{formatWindow(selectedSchedule, projectTimezone)}</p>
              </div>
              <span className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: scheduleColor(selectedSchedule.status) }}>
                {String(selectedSchedule.status).replaceAll("_", " ")}
              </span>
            </div>

            <div className="grid gap-3 rounded-xl bg-gray-50 p-4 text-sm sm:grid-cols-2">
              <div><span className="text-gray-500">Requested by</span><div className="font-medium text-gray-900">{selectedSchedule.requested_by_name || "—"}</div></div>
              <div><span className="text-gray-500">Meeting type</span><div className="font-medium text-gray-900">{roleLabel(selectedSchedule.meeting_type)}</div></div>
              {selectedSchedule.location && (
                <div className="sm:col-span-2">
                  <span className="text-gray-500">Location</span>
                  <div className="break-words font-medium text-gray-900">
                    {selectedSchedule.location}
                  </div>
                </div>
              )}
              <div className="sm:col-span-2">
                <span className="text-gray-500">Participant responses</span>
                <div className="mt-2 space-y-2">
                  {(selectedSchedule.requested_users || []).map((person) => (
                    <div key={person.user_id} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-gray-200">
                      <span className="min-w-0 truncate font-medium text-gray-900">{person.name || person.email || `User #${person.user_id}`}</span>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${responseBadgeClass(person.response_status)}`}>
                        {String(person.response_status || "requested").replaceAll("_", " ")}
                      </span>
                    </div>
                  ))}
                  {!(selectedSchedule.requested_users || []).length && <div className="font-medium text-gray-900">—</div>}
                </div>
                {selectedResponseSummary.total > 1 && (
                  <div className="mt-2 text-xs text-gray-500">
                    {selectedResponseSummary.accepted} accepted · {selectedResponseSummary.rejected} rejected · {selectedResponseSummary.requested} pending
                  </div>
                )}
              </div>
              <div className="sm:col-span-2"><span className="text-gray-500">CC</span><div className="font-medium text-gray-900">{(selectedSchedule.cc_users || []).map((person) => person.name).join(", ") || "—"}</div></div>
              {selectedSchedule.inspection_number && <div><span className="text-gray-500">Inspection</span><div className="font-medium text-gray-900">{selectedSchedule.inspection_number}</div></div>}
              <div><span className="text-gray-500">One-hour reminder</span><div className="font-medium text-gray-900">{selectedSchedule.status === "accepted" ? (selectedSchedule.reminder_sent_at ? "Sent" : "Scheduled") : "Starts after acceptance"}</div></div>
            </div>

            {selectedSchedule.notes && <div className="rounded-xl border border-gray-200 p-4 text-sm text-gray-700">{selectedSchedule.notes}</div>}
            {selectedSchedule.response_note && <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">Latest response: {selectedSchedule.response_note}</div>}
            {selectedCurrentParticipant && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                Your response: <b>{String(selectedCurrentParticipant.response_status || "requested").replaceAll("_", " ")}</b>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
              {canCurrentUserEdit && (
                <button
                  type="button"
                  disabled={statusLoading}
                  onClick={() => setEditOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-orange-200 px-3 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50"
                >
                  <Pencil className="h-4 w-4" /> Edit request
                </button>
              )}
              {selectedSchedule.requested_by_user_id === currentUserId && ["requested", "proposed_new_time", "accepted"].includes(selectedSchedule.status) && (
                <button type="button" disabled={statusLoading} onClick={() => updateStatus("cancelled", { response_note: "Cancelled by requester" })} className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                  <XCircle className="h-4 w-4" /> Cancel
                </button>
              )}
              {canCurrentUserRespond && (
                <>
                  <button type="button" disabled={statusLoading} onClick={() => updateStatus("rejected", { response_note: "Rejected" })} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50">
                    <X className="h-4 w-4" /> Reject
                  </button>
                  <button type="button" disabled={statusLoading} onClick={() => { const start = localFields(selectedSchedule.preferred_start_at, projectTimezone); const end = localFields(selectedSchedule.preferred_end_at, projectTimezone); setProposal({ date: start.date, start: start.time, end: end.time, note: "" }); setProposalOpen(true); }} className="inline-flex items-center gap-2 rounded-xl border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50">
                    <Clock3 className="h-4 w-4" /> Propose time
                  </button>
                  <button type="button" disabled={statusLoading} onClick={() => updateStatus("accepted", { response_note: "Accepted" })} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
                    <CheckCircle2 className="h-4 w-4" /> Accept
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      <WalkScheduleEditModal
        open={editOpen && Boolean(selectedSchedule)}
        onClose={() => setEditOpen(false)}
        projectId={projectId}
        projectTimezone={projectTimezone}
        schedule={selectedSchedule}
        team={team}
        inspections={inspections}
        onSaved={async (updatedSchedule) => {
          setEditOpen(false);
          if (updatedSchedule) {
            setSelectedSchedule(updatedSchedule);
            setSchedules((current) =>
              current.map((schedule) =>
                Number(schedule.id) === Number(updatedSchedule.id)
                  ? updatedSchedule
                  : schedule,
              ),
            );
            setOversightSchedules((current) =>
              current.map((schedule) =>
                Number(schedule.id) === Number(updatedSchedule.id)
                  ? updatedSchedule
                  : schedule,
              ),
            );
          }
          setMessage(
            updatedSchedule?.email_warning || "Meeting request updated.",
          );
          await loadCalendar();
          if (canUseOversight) await loadOversight();
        }}
      />

      <Modal open={proposalOpen} title="Propose a new time" onClose={() => setProposalOpen(false)} width="max-w-lg">
        <form onSubmit={submitProposal} className="space-y-4 p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm font-medium text-gray-700">Date *<input required type="date" value={proposal.date} onChange={(event) => setProposal((current) => ({ ...current, date: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" /></label>
            <label className="text-sm font-medium text-gray-700">From *<input required type="time" value={proposal.start} onChange={(event) => setProposal((current) => ({ ...current, start: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" /></label>
            <label className="text-sm font-medium text-gray-700">To *<input required type="time" value={proposal.end} onChange={(event) => setProposal((current) => ({ ...current, end: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" /></label>
          </div>
          <label className="block text-sm font-medium text-gray-700">Reason<input value={proposal.note} onChange={(event) => setProposal((current) => ({ ...current, note: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5" placeholder="Suggested alternative" /></label>
          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button type="button" onClick={() => setProposalOpen(false)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium">Cancel</button>
            <button disabled={statusLoading} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white">Send proposal</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
